import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, LoadingButton, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { formatTableDate, useNumberInput } from '@redpanda/utils';
import { setReceivableInvoice, clearReceivableInvoice } from '../services/api';

import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { notifyDataChanged } from '../utils/dataEvents';
import { Receivable } from '../stores/pmReceivableStore';

/** 付款期限預設天數（一般月結 30 天） */
const DEFAULT_TERM_DAYS = 30;
const DAY = 24 * 60 * 60 * 1000;

interface FormData {
  invoicedAt: string;
  termDays: string;
}

const addDays = (date: string, days: number) => toDateInput(new Date(new Date(`${date}T00:00:00Z`).getTime() + days * DAY).toISOString());

/**
 * 標記已請款／修改請款：請款日＋付款期限（天）→ 預計收款日
 * 過了預計收款日還沒收齊＝收款逾期，排程會提醒專案負責人；修改時可「取消請款」（回到可請款／未到期）
 */
const InvoiceDialog: React.FC<{ item: Receivable; onClose: () => void }> = ({ item, onClose }) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const daysInput = useNumberInput('integer');
  const editing = !!item.invoicedAt;
  const initialInvoicedAt = editing ? toDateInput(item.invoicedAt) : getTodayDateInput();
  const initialDays = editing && item.paymentDueDate
    ? Math.round((new Date(item.paymentDueDate).getTime() - new Date(`${initialInvoicedAt}T00:00:00Z`).getTime()) / DAY)
    : DEFAULT_TERM_DAYS;

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: { invoicedAt: initialInvoicedAt, termDays: String(initialDays) },
  });
  const invoicedAt = watch('invoicedAt');
  const termDays = Number(watch('termDays') || 0);
  const dueDate = invoicedAt ? addDays(invoicedAt, termDays) : '';

  const save = async (request: () => Promise<unknown>, successKey: string) => {
    setSaving(true);
    try {
      await request();
      toast.success(t(successKey, { ns: 'pages' }));
      notifyDataChanged('payments'); // 應收帳款、儀表板重抓
      onClose();
    } catch (error: any) {
      console.error('Failed to save invoice:', error);
      toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };
  const onSubmit = (data: FormData) => save(
    () => setReceivableInvoice(item.id, { invoicedAt: toISOString(data.invoicedAt)!, dueDate: toISOString(addDays(data.invoicedAt, Number(data.termDays)))! }),
    'pmReceivables.invoice.saved'
  );

  return (
    <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-md">
      <DialogHeader
        title={t(editing ? 'pmReceivables.invoice.editTitle' : 'pmReceivables.invoice.title', { ns: 'pages' })}
        onClose={onClose}
        closeDisabled={saving}
        closeLabel={t('common.actions.close')}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-5">
          <p className="text-sm text-fg-secondary">
            {item.projectName}・{item.milestoneName ?? item.packageName ?? t('pmReceivables.wholeProject', { ns: 'pages' })}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <FormFieldWithError label={t('pmReceivables.invoice.invoicedAt', { ns: 'pages' })} required error={errors.invoicedAt?.message} htmlFor="invoicedAt">
              <Input
                type="date"
                id="invoicedAt"
                {...register('invoicedAt', { required: t('pmReceivables.invoice.invoicedAtRequired', { ns: 'pages' }) })}
                variant={errors.invoicedAt ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('pmReceivables.invoice.termDays', { ns: 'pages' })} required error={errors.termDays?.message} htmlFor="termDays">
              <Input
                type="text"
                id="termDays"
                inputMode="numeric"
                {...daysInput.bind(register('termDays', {
                  required: t('pmReceivables.invoice.termDaysRequired', { ns: 'pages' }),
                  ...daysInput.validationRules,
                  max: { value: 365, message: t('pmReceivables.invoice.termDaysMax', { ns: 'pages' }) },
                }))}
                variant={errors.termDays ? 'error' : 'default'}
              />
            </FormFieldWithError>
          </div>
          <p className="text-sm text-fg">
            {t('pmReceivables.invoice.dueDate', { ns: 'pages' })}
            <span className="ml-2 font-medium tabular-nums">{dueDate ? formatTableDate(toISOString(dueDate)!, i18n.language) : '—'}</span>
          </p>
        </div>
        <DialogFooter>
          {editing && (
            <button type="button" onClick={() => setConfirmClear(true)} disabled={saving} className="mr-auto text-sm text-red-600 hover:underline disabled:opacity-50 dark:text-red-400">
              {t('pmReceivables.invoice.clear', { ns: 'pages' })}
            </button>
          )}
          <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            loading={saving}
            isEditMode={editing}
            customText={{ create: t('common.actions.save'), createLoading: t('common.status.saving') }}
            t={t}
          />
        </DialogFooter>
      </form>
      <ConfirmModal
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => { setConfirmClear(false); save(() => clearReceivableInvoice(item.id), 'pmReceivables.invoice.cleared'); }}
        title={t('pmReceivables.invoice.clear', { ns: 'pages' })}
        message={t('pmReceivables.invoice.clearConfirm', { ns: 'pages' })}
        type="danger"
        t={t}
      />
    </DialogShell>
  );
};

export default InvoiceDialog;
